import BotonManualHeader from "../../components/BotonManualHeader";
import React, { useState, useEffect } from "react";
import {
    Container,
    Card,
    Row,
    Col,
    Button,
    Badge,
    Form,
    Spinner,
    Alert,
    Modal,
    Table,
    ButtonGroup
} from "react-bootstrap";
import {
    FaCalendarAlt,
    FaChevronLeft,
    FaChevronRight,
    FaFilter,
    FaList,
    FaCalendar,
    FaUser,
    FaFileExcel
} from "react-icons/fa";
import {
    getFirestore,
    collection,
    collectionGroup,
    query,
    where,
    getDocs
} from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import * as XLSX from "xlsx";

const db = getFirestore(firebaseApp);

// Tipos de ausencia con colores
const TIPOS_AUSENCIA = {
    DescansoMedico: { label: "Descanso Médico", color: "#EF4444", bg: "#FEE2E2" },
    Permisos: { label: "Permiso Personal", color: "#F59E0B", bg: "#FEF3C7" },
    Paternidad: { label: "Lic. Paternidad", color: "#3B82F6", bg: "#DBEAFE" },
    Maternidad: { label: "Lic. Maternidad", color: "#EC4899", bg: "#FCE7F3" },
    Vacaciones: { label: "Vacaciones", color: "#10B981", bg: "#D1FAE5" },
    Otros: { label: "Otros", color: "#8B5CF6", bg: "#EDE9FE" }
};

const ESTADOS = {
    pendiente: { label: "Pendiente", variant: "warning" },
    aprobado: { label: "Aprobado", variant: "success" },
    rechazado: { label: "Rechazado", variant: "danger" }
};

function CalendarioAusencias({ usuario }) {
    const [mesActual, setMesActual] = useState(new Date());
    const [ausencias, setAusencias] = useState([]);
    const [ausenciasFiltradas, setAusenciasFiltradas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [vista, setVista] = useState("calendario"); // "calendario" | "lista"

    // Filtros
    const [filtros, setFiltros] = useState({
        tipo: "",
        estado: "",
        empleado: ""
    });

    // Modal
    const [showModal, setShowModal] = useState(false);
    const [ausenciaSeleccionada, setAusenciaSeleccionada] = useState(null);
    const [ausenciasDia, setAusenciasDia] = useState([]);

    useEffect(() => {
        cargarAusencias();
    }, [mesActual, usuario.ruc]);

    useEffect(() => {
        aplicarFiltros();
    }, [ausencias, filtros]);

    const cargarAusencias = async () => {
        setLoading(true);
        try {
            const ruc = usuario.ruc || usuario.rucEmpresa;

            // Rango del mes
            const primerDia = new Date(
                mesActual.getFullYear(),
                mesActual.getMonth(),
                1
            );
            const ultimoDia = new Date(
                mesActual.getFullYear(),
                mesActual.getMonth() + 1,
                0,
                23,
                59,
                59
            );

            // Buscar solicitudes aprobadas
            const solicitudesRef = collection(db, "solicitudes_ausencia");
            const q = query(
                solicitudesRef,
                where("rucEmpresa", "==", ruc),
                where("fechaInicio", "<=", ultimoDia)
            );

            const querySnapshot = await getDocs(q);

            const ausenciasArr = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                const fechaInicio = data.fechaInicio?.toDate
                    ? data.fechaInicio.toDate()
                    : new Date(data.fechaInicio);
                const fechaFin = data.fechaFin?.toDate
                    ? data.fechaFin.toDate()
                    : new Date(data.fechaFin);

                // Verificar solapamiento con el mes
                if (fechaFin >= primerDia && fechaInicio <= ultimoDia) {
                    ausenciasArr.push({
                        id: doc.id,
                        ...data,
                        fechaInicio,
                        fechaFin
                    });
                }
            });

            setAusencias(ausenciasArr);
        } catch (error) {
            console.error("Error al cargar ausencias:", error);
        } finally {
            setLoading(false);
        }
    };

    const aplicarFiltros = () => {
        let filtered = [...ausencias];

        if (filtros.tipo) {
            filtered = filtered.filter((a) => a.tipo === filtros.tipo);
        }

        if (filtros.estado) {
            filtered = filtered.filter((a) => a.estado === filtros.estado);
        }

        if (filtros.empleado) {
            filtered = filtered.filter((a) =>
                a.nombreEmpleado
                    ?.toLowerCase()
                    .includes(filtros.empleado.toLowerCase())
            );
        }

        setAusenciasFiltradas(filtered);
    };

    const cambiarMes = (direccion) => {
        const nuevoMes = new Date(mesActual);
        nuevoMes.setMonth(mesActual.getMonth() + direccion);
        setMesActual(nuevoMes);
    };

    const obtenerDiasDelMes = () => {
        const year = mesActual.getFullYear();
        const month = mesActual.getMonth();

        const primerDia = new Date(year, month, 1);
        const ultimoDia = new Date(year, month + 1, 0);

        const dias = [];
        const primerDiaSemana = primerDia.getDay();

        // Días del mes anterior (para completar la primera semana)
        for (let i = primerDiaSemana - 1; i >= 0; i--) {
            const dia = new Date(year, month, -i);
            dias.push({ fecha: dia, esOtroMes: true });
        }

        // Días del mes actual
        for (let i = 1; i <= ultimoDia.getDate(); i++) {
            const dia = new Date(year, month, i);
            dias.push({ fecha: dia, esOtroMes: false });
        }

        // Días del mes siguiente (para completar la última semana)
        const diasRestantes = 42 - dias.length; // 6 semanas * 7 días
        for (let i = 1; i <= diasRestantes; i++) {
            const dia = new Date(year, month + 1, i);
            dias.push({ fecha: dia, esOtroMes: true });
        }

        return dias;
    };

    const obtenerAusenciasPorDia = (fecha) => {
        return ausenciasFiltradas.filter((ausencia) => {
            const fechaInicio = new Date(ausencia.fechaInicio);
            const fechaFin = new Date(ausencia.fechaFin);

            fechaInicio.setHours(0, 0, 0, 0);
            fechaFin.setHours(23, 59, 59, 999);
            fecha.setHours(12, 0, 0, 0);

            return fecha >= fechaInicio && fecha <= fechaFin;
        });
    };

    const handleDiaClick = (fecha, ausenciasDia) => {
        if (ausenciasDia.length > 0) {
            setAusenciasDia(ausenciasDia);
            setShowModal(true);
        }
    };

    const exportarExcel = () => {
        const datos = ausenciasFiltradas.map((a) => ({
            Empleado: a.nombreEmpleado,
            DNI: a.empleadoDni,
            Tipo: TIPOS_AUSENCIA[a.tipo]?.label || a.tipo,
            "Fecha Inicio": a.fechaInicio.toLocaleDateString("es-PE"),
            "Fecha Fin": a.fechaFin.toLocaleDateString("es-PE"),
            Días: a.totalDias || 0,
            Estado: a.estado,
            Motivo: a.motivo
        }));

        const ws = XLSX.utils.json_to_sheet(datos);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Ausencias");

        const nombreArchivo = `Calendario_Ausencias_${mesActual.toLocaleDateString(
            "es-PE",
            { year: "numeric", month: "long" }
        )}.xlsx`;
        XLSX.writeFile(wb, nombreArchivo);
    };

    const esHoy = (fecha) => {
        const hoy = new Date();
        return (
            fecha.getDate() === hoy.getDate() &&
            fecha.getMonth() === hoy.getMonth() &&
            fecha.getFullYear() === hoy.getFullYear()
        );
    };

    const dias = obtenerDiasDelMes();
    const nombreMes = mesActual.toLocaleDateString("es-PE", {
        month: "long",
        year: "numeric"
    });

    return (
        <div
            style={{
                background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
                minHeight: "100vh",
                width: "100vw",
                maxWidth: "100%",
                padding: "20px 0"
            }}
        >
            <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
                <Card
                    className="shadow-lg border-0"
                    style={{ borderRadius: "15px", overflow: "hidden" }}
                >
                    <Card.Header
                        className="text-white py-4 position-relative"
                        style={{
                            background: "linear-gradient(45deg, #2c3e50, #3498db)",
                            borderBottom: "3px solid #f39c12"
                        }}
                    >
          <div
            className="position-absolute end-0 top-50 translate-middle-y me-4"
            style={{ zIndex: 10 }}
          >
            <BotonManualHeader />
          </div>
                        <div className="text-center">
                            <h2 className="fw-bold mb-1">
                                <FaCalendarAlt className="me-3" />
                                Calendario de Ausencias
                            </h2>
                            <p className="mb-0 opacity-75">
                                Visualice y gestione las ausencias del personal
                            </p>
                        </div>
                    </Card.Header>

                    <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
                        {/* Controles y Filtros */}
                        <Card className="shadow-sm border-0 mb-4">
                            <Card.Body className="p-3">
                                <Row className="align-items-center">
                                    {/* Navegación de Mes */}
                                    <Col md={4}>
                                        <div className="d-flex align-items-center gap-2">
                                            <Button
                                                variant="outline-primary"
                                                onClick={() => cambiarMes(-1)}
                                                disabled={loading}
                                            >
                                                <FaChevronLeft />
                                            </Button>
                                            <h5 className="mb-0 text-center flex-grow-1 text-capitalize">
                                                {nombreMes}
                                            </h5>
                                            <Button
                                                variant="outline-primary"
                                                onClick={() => cambiarMes(1)}
                                                disabled={loading}
                                            >
                                                <FaChevronRight />
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                onClick={() => setMesActual(new Date())}
                                                disabled={loading}
                                            >
                                                Hoy
                                            </Button>
                                        </div>
                                    </Col>

                                    {/* Vista */}
                                    <Col md={4} className="text-center">
                                        <ButtonGroup>
                                            <Button
                                                variant={
                                                    vista === "calendario"
                                                        ? "primary"
                                                        : "outline-primary"
                                                }
                                                onClick={() => setVista("calendario")}
                                            >
                                                <FaCalendar className="me-2" />
                                                Calendario
                                            </Button>
                                            <Button
                                                variant={
                                                    vista === "lista" ? "primary" : "outline-primary"
                                                }
                                                onClick={() => setVista("lista")}
                                            >
                                                <FaList className="me-2" />
                                                Lista
                                            </Button>
                                        </ButtonGroup>
                                    </Col>

                                    {/* Exportar */}
                                    <Col md={4} className="text-end">
                                        <Button
                                            variant="success"
                                            onClick={exportarExcel}
                                            disabled={loading || ausenciasFiltradas.length === 0}
                                        >
                                            <FaFileExcel className="me-2" />
                                            Exportar Excel
                                        </Button>
                                    </Col>
                                </Row>

                                {/* Filtros */}
                                <Row className="mt-3">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">
                                                <FaFilter className="me-1" />
                                                Tipo de Ausencia
                                            </Form.Label>
                                            <Form.Select
                                                size="sm"
                                                value={filtros.tipo}
                                                onChange={(e) =>
                                                    setFiltros({ ...filtros, tipo: e.target.value })
                                                }
                                            >
                                                <option value="">Todos</option>
                                                {Object.entries(TIPOS_AUSENCIA).map(([key, value]) => (
                                                    <option key={key} value={key}>
                                                        {value.label}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">Estado</Form.Label>
                                            <Form.Select
                                                size="sm"
                                                value={filtros.estado}
                                                onChange={(e) =>
                                                    setFiltros({ ...filtros, estado: e.target.value })
                                                }
                                            >
                                                <option value="">Todos</option>
                                                {Object.entries(ESTADOS).map(([key, value]) => (
                                                    <option key={key} value={key}>
                                                        {value.label}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">
                                                <FaUser className="me-1" />
                                                Empleado
                                            </Form.Label>
                                            <Form.Control
                                                size="sm"
                                                type="text"
                                                placeholder="Buscar por nombre..."
                                                value={filtros.empleado}
                                                onChange={(e) =>
                                                    setFiltros({ ...filtros, empleado: e.target.value })
                                                }
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Resumen */}
                                <Row className="mt-3">
                                    <Col>
                                        <Alert variant="info" className="mb-0 py-2">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span>
                                                    <strong>Total ausencias:</strong>{" "}
                                                    {ausenciasFiltradas.length}
                                                </span>
                                                <span>
                                                    <strong>Empleados afectados:</strong>{" "}
                                                    {
                                                        new Set(
                                                            ausenciasFiltradas.map((a) => a.empleadoDni)
                                                        ).size
                                                    }
                                                </span>
                                            </div>
                                        </Alert>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Loading */}
                        {loading && (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 text-muted">Cargando ausencias...</p>
                            </div>
                        )}

                        {/* Vista Calendario */}
                        {!loading && vista === "calendario" && (
                            <Card className="shadow-sm border-0">
                                <Card.Body className="p-0">
                                    {/* Encabezado de días */}
                                    <div
                                        className="d-grid"
                                        style={{
                                            gridTemplateColumns: "repeat(7, 1fr)",
                                            backgroundColor: "#3498db",
                                            color: "white"
                                        }}
                                    >
                                        {["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"].map(
                                            (dia) => (
                                                <div
                                                    key={dia}
                                                    className="text-center fw-bold py-2 border-end border-light"
                                                >
                                                    {dia}
                                                </div>
                                            )
                                        )}
                                    </div>

                                    {/* Días del mes */}
                                    <div
                                        className="d-grid"
                                        style={{
                                            gridTemplateColumns: "repeat(7, 1fr)",
                                            gridAutoRows: "minmax(100px, auto)"
                                        }}
                                    >
                                        {dias.map((dia, index) => {
                                            const ausenciasDia = obtenerAusenciasPorDia(dia.fecha);
                                            const hoy = esHoy(dia.fecha);

                                            return (
                                                <div
                                                    key={index}
                                                    className={`border p-2 ${dia.esOtroMes ? "bg-light" : ""
                                                        } ${hoy ? "border-primary border-2" : ""} ${ausenciasDia.length > 0
                                                            ? "cursor-pointer"
                                                            : ""
                                                        }`}
                                                    style={{
                                                        minHeight: "100px",
                                                        cursor:
                                                            ausenciasDia.length > 0 ? "pointer" : "default",
                                                        position: "relative"
                                                    }}
                                                    onClick={() => handleDiaClick(dia.fecha, ausenciasDia)}
                                                >
                                                    <div
                                                        className={`fw-bold ${dia.esOtroMes ? "text-muted" : ""
                                                            } ${hoy ? "text-primary" : ""}`}
                                                    >
                                                        {dia.fecha.getDate()}
                                                    </div>

                                                    {/* Ausencias del día */}
                                                    <div className="mt-1">
                                                        {ausenciasDia.slice(0, 3).map((ausencia, idx) => {
                                                            const tipoInfo =
                                                                TIPOS_AUSENCIA[ausencia.tipo] || {};
                                                            return (
                                                                <div
                                                                    key={idx}
                                                                    className="small mb-1 px-1 rounded text-truncate"
                                                                    style={{
                                                                        backgroundColor: tipoInfo.bg,
                                                                        borderLeft: `3px solid ${tipoInfo.color}`,
                                                                        fontSize: "0.7rem"
                                                                    }}
                                                                    title={`${ausencia.nombreEmpleado} - ${tipoInfo.label
                                                                        }`}
                                                                >
                                                                    {ausencia.nombreEmpleado?.split(" ")[0]}
                                                                </div>
                                                            );
                                                        })}
                                                        {ausenciasDia.length > 3 && (
                                                            <div
                                                                className="small text-muted"
                                                                style={{ fontSize: "0.7rem" }}
                                                            >
                                                                +{ausenciasDia.length - 3} más
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Card.Body>
                            </Card>
                        )}

                        {/* Vista Lista */}
                        {!loading && vista === "lista" && (
                            <Card className="shadow-sm border-0">
                                <Card.Body className="p-0">
                                    <div style={{ maxHeight: "600px", overflowY: "auto" }}>
                                        <Table responsive hover className="mb-0">
                                            <thead className="table-light sticky-top">
                                                <tr>
                                                    <th>Empleado</th>
                                                    <th>Tipo</th>
                                                    <th>Fecha Inicio</th>
                                                    <th>Fecha Fin</th>
                                                    <th className="text-center">Días</th>
                                                    <th>Estado</th>
                                                    <th>Motivo</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {ausenciasFiltradas.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={7} className="text-center py-4">
                                                            No hay ausencias para mostrar
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    ausenciasFiltradas.map((ausencia) => {
                                                        const tipoInfo =
                                                            TIPOS_AUSENCIA[ausencia.tipo] || {};
                                                        const estadoInfo =
                                                            ESTADOS[ausencia.estado] || {};

                                                        return (
                                                            <tr key={ausencia.id}>
                                                                <td>
                                                                    <div className="fw-bold">
                                                                        {ausencia.nombreEmpleado}
                                                                    </div>
                                                                    <small className="text-muted">
                                                                        DNI: {ausencia.empleadoDni}
                                                                    </small>
                                                                </td>
                                                                <td>
                                                                    <Badge
                                                                        style={{
                                                                            backgroundColor: tipoInfo.color
                                                                        }}
                                                                    >
                                                                        {tipoInfo.label}
                                                                    </Badge>
                                                                </td>
                                                                <td>
                                                                    {ausencia.fechaInicio.toLocaleDateString(
                                                                        "es-PE"
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {ausencia.fechaFin.toLocaleDateString(
                                                                        "es-PE"
                                                                    )}
                                                                </td>
                                                                <td className="text-center">
                                                                    <Badge bg="info">
                                                                        {ausencia.totalDias || 0}
                                                                    </Badge>
                                                                </td>
                                                                <td>
                                                                    <Badge bg={estadoInfo.variant}>
                                                                        {estadoInfo.label}
                                                                    </Badge>
                                                                </td>
                                                                <td
                                                                    className="text-truncate"
                                                                    style={{ maxWidth: "200px" }}
                                                                    title={ausencia.motivo}
                                                                >
                                                                    {ausencia.motivo}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card.Body>
                            </Card>
                        )}

                        {/* Leyenda */}
                        <Card className="shadow-sm border-0 mt-4">
                            <Card.Body className="p-3">
                                <h6 className="fw-bold mb-3">Leyenda de Tipos</h6>
                                <Row>
                                    {Object.entries(TIPOS_AUSENCIA).map(([key, value]) => (
                                        <Col key={key} md={4} className="mb-2">
                                            <div className="d-flex align-items-center">
                                                <div
                                                    style={{
                                                        width: "20px",
                                                        height: "20px",
                                                        backgroundColor: value.color,
                                                        borderRadius: "4px",
                                                        marginRight: "10px"
                                                    }}
                                                />
                                                <span className="small">{value.label}</span>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </Card.Body>
                        </Card>
                    </Card.Body>
                </Card>
            </Container>

            {/* Modal de Detalle del Día */}
            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                size="lg"
                centered
            >
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title>
                        <FaCalendarAlt className="me-2" />
                        Ausencias del Día
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {ausenciasDia.length === 0 ? (
                        <Alert variant="info">No hay ausencias en este día</Alert>
                    ) : (
                        <Table responsive hover>
                            <thead>
                                <tr>
                                    <th>Empleado</th>
                                    <th>Tipo</th>
                                    <th>Estado</th>
                                    <th>Período</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ausenciasDia.map((ausencia) => {
                                    const tipoInfo = TIPOS_AUSENCIA[ausencia.tipo] || {};
                                    const estadoInfo = ESTADOS[ausencia.estado] || {};

                                    return (
                                        <tr key={ausencia.id}>
                                            <td>
                                                <div className="fw-bold">
                                                    {ausencia.nombreEmpleado}
                                                </div>
                                                <small className="text-muted">
                                                    {ausencia.empleadoDni}
                                                </small>
                                            </td>
                                            <td>
                                                <Badge style={{ backgroundColor: tipoInfo.color }}>
                                                    {tipoInfo.label}
                                                </Badge>
                                            </td>
                                            <td>
                                                <Badge bg={estadoInfo.variant}>
                                                    {estadoInfo.label}
                                                </Badge>
                                            </td>
                                            <td className="small">
                                                {ausencia.fechaInicio.toLocaleDateString("es-PE")} -{" "}
                                                {ausencia.fechaFin.toLocaleDateString("es-PE")}
                                                <br />
                                                <Badge bg="info" className="mt-1">
                                                    {ausencia.totalDias || 0} días
                                                </Badge>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default CalendarioAusencias;
